import React from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { DropZone } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import {
    SUPPORTED_IMAGE_ACCEPT,
    SUPPORTED_IMAGE_MIME_TYPES,
    type CropSize,
    type ImageCropUploadValue,
    type ImageUploadPayload,
} from './imageCropUploadHelpers';
import { useImageCropUpload } from './useImageCropUpload';

type ImageCropUploadProps = {
    applyLabel?: string;
    aspectClassName?: string;
    /**
     * Width/positioning classes for the upload box (the empty dropzone, the
     * existing-image preview, and the crop area all share it). Defaults to
     * `'w-full'`. Forms that only need a small thumbnail pass e.g.
     * `'mx-auto w-[150px]'` to render a compact, centered box instead of a
     * full-width one.
     */
    boxClassName?: string;
    changeLabel?: string;
    cropErrorMessage?: string;
    cropSize?: CropSize;
    description?: string;
    id: string;
    invalidSizeMessage?: string;
    invalidTypeMessage?: string;
    label?: string;
    readErrorMessage?: string;
    mode?: 'deferred' | 'immediate';
    onChange: (value: ImageCropUploadValue | null) => void;
    onPendingCropChange?: (pending: boolean) => void;
    onUpload?: (
        payload: ImageUploadPayload,
    ) => Promise<{ imageName: string; imageUrl: string }>;
    removeLabel?: string;
    selectLabel?: string;
    value: ImageCropUploadValue | null;
};

const NO_IMAGE_ICON = 'carbon:no-image';
const UPLOAD_ICON = 'gravity-ui:arrow-up-from-line';
const REMOVE_ICON = 'gravity-ui:xmark';

const getDropOperation = (types: { has: (type: string) => boolean }) =>
    SUPPORTED_IMAGE_MIME_TYPES.some((type) => types.has(type))
        ? 'copy'
        : 'cancel';

export const ImageCropUpload: React.FC<ImageCropUploadProps> = ({
    applyLabel = 'Apply crop',
    aspectClassName = 'aspect-video',
    boxClassName = 'w-full',
    changeLabel = 'Change image',
    cropErrorMessage = 'Could not crop the image. Please try again.',
    cropSize,
    description = 'Drag an image here, or select one to upload.',
    id,
    invalidSizeMessage = 'Image exceeds the 5 MB size limit.',
    invalidTypeMessage = 'Unsupported file type. Use JPEG, PNG, or WebP.',
    label = 'Upload image',
    readErrorMessage = 'Could not read the image. Please try another file.',
    mode = 'deferred',
    onChange,
    onPendingCropChange,
    onUpload,
    removeLabel = 'Remove image',
    selectLabel = 'Select image',
    value,
}) => {
    const {
        cropNaturalSize,
        cropRect,
        error,
        isApplying,
        onApplyCrop,
        onCropPointerDown,
        onCropPointerMove,
        onCropPointerUp,
        onImageLoad,
        onRemove,
        onSelectFiles,
        selection,
    } = useImageCropUpload({
        cropSize,
        mode,
        onChange,
        onPendingCropChange,
        onUpload,
    });

    const onDrop = React.useCallback(
        async (event: {
            items: Array<{ kind: string; getFile?: () => Promise<File> }>;
        }) => {
            const dropped: File[] = [];
            for (const item of event.items) {
                if (item.kind === 'file' && item.getFile) {
                    dropped.push(await item.getFile());
                }
            }
            if (dropped.length > 0) {
                onSelectFiles(dropped);
            }
        },
        [onSelectFiles],
    );

    if (selection) {
        return (
            <div
                className="flex w-full flex-col gap-3"
                {...getNodeTestIdProp(id)}
            >
                <div
                    className={`relative ${boxClassName} overflow-hidden rounded-xl bg-surface-secondary`}
                    onPointerMove={onCropPointerMove}
                    onPointerUp={onCropPointerUp}
                    {...getNodeTestIdProp(`${id}-crop-area`)}
                >
                    <img
                        alt={selection.fileName}
                        className="block h-auto w-full select-none"
                        draggable={false}
                        onLoad={(event) =>
                            onImageLoad({
                                width: event.currentTarget.clientWidth,
                                height: event.currentTarget.clientHeight,
                            })
                        }
                        src={selection.dataUrl}
                        {...getNodeTestIdProp(`${id}-crop-image`)}
                    />
                    {cropRect ? (
                        <div
                            className="absolute cursor-move touch-none border-2 border-accent shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
                            onPointerDown={(event) =>
                                onCropPointerDown(event, 'move')
                            }
                            style={{
                                left: cropRect.x,
                                top: cropRect.y,
                                width: cropRect.width,
                                height: cropRect.height,
                            }}
                            {...getNodeTestIdProp(`${id}-crop-rect`)}
                        >
                            <span
                                className="absolute left-1 top-1 rounded-md bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white tabular-nums"
                                {...getNodeTestIdProp(`${id}-crop-dimensions`)}
                            >
                                {cropNaturalSize.width} ×{' '}
                                {cropNaturalSize.height}
                            </span>
                            <div
                                className="absolute -bottom-1.5 -right-1.5 size-4 cursor-se-resize touch-none rounded-full border-2 border-white bg-accent"
                                onPointerDown={(event) =>
                                    onCropPointerDown(event, 'resize')
                                }
                                {...getNodeTestIdProp(
                                    `${id}-crop-resize-handle`,
                                )}
                            />
                        </div>
                    ) : null}
                </div>
                {error === 'crop' ? (
                    <p
                        className="text-sm text-danger"
                        {...getNodeTestIdProp(`${id}-crop-error`)}
                    >
                        {cropErrorMessage}
                    </p>
                ) : null}
                <div
                    className="flex justify-end"
                    {...getNodeTestIdProp(`${id}-crop-actions`)}
                >
                    <Button
                        isPending={isApplying}
                        onPress={onApplyCrop}
                        variant="secondary"
                        {...getNodeTestIdProp(`${id}-apply-button`)}
                    >
                        {applyLabel}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <DropZone className="w-full" {...getNodeTestIdProp(id)}>
            <DropZone.Area
                className={`${boxClassName} ${aspectClassName}`}
                getDropOperation={getDropOperation}
                onDrop={onDrop}
                {...getNodeTestIdProp(`${id}-area`)}
            >
                {value ? (
                    <div
                        className="group relative size-full overflow-hidden rounded-xl"
                        {...getNodeTestIdProp(`${id}-existing`)}
                    >
                        <img
                            alt={value.imageName}
                            className="size-full object-cover"
                            src={value.imageUrl}
                            {...getNodeTestIdProp(`${id}-existing-image`)}
                        />
                        <DropZone.Trigger
                            aria-label={changeLabel}
                            // The base `.drop-zone__trigger` is a styled pill (mt-1,
                            // border, px-4, fixed height). Reset that frame so this
                            // acts as a true full-bleed overlay — the mt-1 margin
                            // otherwise shifts the cover down and leaves the image
                            // edge uncovered.
                            className="absolute inset-0 m-0 flex size-full items-center justify-center border-0 bg-black/0 p-0 text-white transition-colors group-hover:bg-black/45"
                            {...getNodeTestIdProp(`${id}-change-trigger`)}
                        >
                            <Icon
                                className="size-7 opacity-0 transition-opacity group-hover:opacity-100"
                                icon={UPLOAD_ICON}
                            />
                        </DropZone.Trigger>
                        <Button
                            aria-label={removeLabel}
                            className="absolute right-2 top-2 z-10 bg-black/55 text-white backdrop-blur-sm hover:bg-black/70"
                            isIconOnly
                            onPress={onRemove}
                            size="sm"
                            variant="tertiary"
                            {...getNodeTestIdProp(`${id}-remove-button`)}
                        >
                            <Icon icon={REMOVE_ICON} />
                        </Button>
                    </div>
                ) : (
                    <>
                        <DropZone.Icon
                            {...getNodeTestIdProp(`${id}-empty-icon`)}
                        >
                            <Icon className="size-7" icon={NO_IMAGE_ICON} />
                        </DropZone.Icon>
                        <DropZone.Label
                            {...getNodeTestIdProp(`${id}-empty-label`)}
                        >
                            {label}
                        </DropZone.Label>
                        <DropZone.Description
                            {...getNodeTestIdProp(`${id}-empty-description`)}
                        >
                            {description}
                        </DropZone.Description>
                        <DropZone.Trigger
                            {...getNodeTestIdProp(`${id}-empty-trigger`)}
                        >
                            {selectLabel}
                        </DropZone.Trigger>
                    </>
                )}
                {/* Selection errors render outside the value split so an
                    invalid *replacement* image (when one is already set)
                    surfaces feedback instead of failing silently. */}
                {error === 'type' || error === 'size' || error === 'read' ? (
                    <p
                        className="mt-1 text-sm text-danger"
                        {...getNodeTestIdProp(`${id}-selection-error`)}
                    >
                        {error === 'type'
                            ? invalidTypeMessage
                            : error === 'size'
                              ? invalidSizeMessage
                              : readErrorMessage}
                    </p>
                ) : null}
            </DropZone.Area>
            <DropZone.Input
                accept={SUPPORTED_IMAGE_ACCEPT}
                onSelect={onSelectFiles}
                {...getNodeTestIdProp(`${id}-input`)}
            />
        </DropZone>
    );
};
